import { createSlice } from '@reduxjs/toolkit';
// utils
import { chatAppOrgRegister } from '../../services/register/chatAppOrgRegister';
import { updatePartnerApi } from '../../services/register/updatePartner';
import axios from '../../utils/axios';
import { createUserUpp } from '../../services/user/createUserUpp';
import { getPartnerDetailsFromDomain } from '../../services/register/getPartnerDetailsFromDomain';
import { createPartner } from '../../services/register/createPartner';
import { createOrg } from '../../services/register/createOrg';
import { addUserToOrg } from '../../services/register/addUserToOrg';
import { extractPhoneCode } from '../../components/hook-form';

const initialState = {
  isLoading: false,
  error: null,
  partners: [], // partners === resellers from api
  paginatedPartners: { data: [], pagination: null },
  initialized: false,
  partnerProfileInitialized: false,
  partnerProfileDetails: {},
};

const partnersSlice = createSlice({
  name: 'partners',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PARTNERS SUCCESS
    getPartnersSuccess(state, action) {
      const resellers = action.payload;
      state.isLoading = false;
      state.initialized = true;
      state.partners = resellers;
    },
    // GET PARTNER UPDATE
    getPartnerUpdate(state, action) {
      state.isLoading = false;
      state.initialized = true;
    },

    // GET PAGINATED PARTNERS SUCCESS
    getPaginatedPartnersSuccess(state, action) {
      state.isLoading = false;
      state.initialized = true;
      state.paginatedPartners = action.payload;
    },

    // UPDATE PARTNERS STATUS
    updatePartnerStatus(state, action) {
      const reseller = action.payload;
      state.isLoading = false;
      state.initialized = true;
      const updated = [...state.partners];
      const toUpdate = updated.findIndex(
        (resellerItem) => resellerItem.reseller_id === reseller.reseller_id
      );
      updated[toUpdate].status = reseller.status;
      state.partners = updated;
    },
    // UPDATE PARTNERSPROFILEDETAILS STATUS
    updatepartnerProfileDetailsSuccess(state, action) {
      const profileDetails = action.payload;
      state.isLoading = false;
      state.partnerProfileInitialized = true;
      state.partnerProfileDetails = profileDetails;
    },
  },
});

// Actions
export const { getPartnersSuccess } = partnersSlice.actions;

// Reducer
export default partnersSlice.reducer;

export const getAllPartners = (state) => state.partners;

export const getPaginatedPartners = (state) => state.partners.paginatedPartners;

export function getPartners(search, startDate, endDate) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      const partnerId = JSON.parse(localStorage.getItem('currentPartner'))._id;
      const params = {};

      if (search) {
        params.search = search;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await axios({
        method: 'get',
        url: `/partners/${partnerId}/get-all-children`,
        params,
      });
      dispatch(partnersSlice.actions.getPartnersSuccess(response.data));
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
    }
  };
}

export function getPartnersPaginated(page = 1, perPage = 25, search, startDate, endDate, status) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      const partnerId = JSON.parse(localStorage.getItem('currentPartner'))._id;
      const params = { page, perPage };

      if (search) {
        params.search = search;
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
      if (status !== undefined && status !== 'all') {
        params.status = status;
      }

      const response = await axios({
        method: 'get',
        url: `/partners/${partnerId}/get-all-children-paginated`,
        params,
      });
      // response.data === { data: [...partners], pagination: {...} }
      dispatch(partnersSlice.actions.getPaginatedPartnersSuccess(response.data));
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
    }
  };
}

export function changeStatus(status, _id) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      const partnerFormData = new FormData();
      partnerFormData.append('status', !status);

      await updatePartnerApi(partnerFormData, _id);
      dispatch(partnersSlice.actions.getPartnerUpdate());
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
    }
  };
}

export function addPartner(data) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      // creating user (POST /users — keeps parent admin session; do not use /auth/register)
      const user = await createUserUpp({
        full_name: data.userName,
        email: data.email,
        country_code: extractPhoneCode(data.country),
        phone: data.phoneNumber,
        password: data.password,
      });

      const parentPartnerId = JSON.parse(
        localStorage.getItem('currentPartner') || 'null'
      )?._id;

      // creating partner
      const partnerFormData = new FormData();
      partnerFormData.append('name', data.name);
      partnerFormData.append('email', data.email);
      partnerFormData.append('phone', data.phoneNumber);
      partnerFormData.append('country_code', extractPhoneCode(data.country));
      partnerFormData.append('password', data.password);
      partnerFormData.append('partner_type', data.resellerType);
      partnerFormData.append('parent', parentPartnerId);
      partnerFormData.append('user', user._id);

      const part = await createPartner(partnerFormData, user._id);

      // creating org
      const orgFormData = new FormData();
      orgFormData.append('name', data.name);
      orgFormData.append('email', data.email);
      orgFormData.append('phone', data.phoneNumber);
      orgFormData.append('country_code', extractPhoneCode(data.country));
      orgFormData.append('partner', part._id);

      const org = await createOrg(orgFormData, user._id);

      // adding user to org
      const obj = {
        user: user._id,
        partner: part._id,
        role: 'admin',
      };
      await addUserToOrg(obj, org._id);

      await chatAppOrgRegisterHelper(org, user);

      dispatch(partnersSlice.actions.getPartnerUpdate());
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
      throw new Error(error?.message);
    }
  };
}

const chatAppOrgRegisterHelper = async (org, user) => {
  const chatApp = await chatAppOrgRegister(org, user);
  return chatApp;
};

export function updatePartner(data, currentUser) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      const partnerFormData = new FormData();
      partnerFormData.append('name', data.name);
      partnerFormData.append('email', data.email);
      partnerFormData.append('phone', data.phoneNumber);
      if (data.country) {
        partnerFormData.append('country_code', extractPhoneCode(data.country));
      }
      partnerFormData.append('partner_type', data.resellerType);
      partnerFormData.append('city', data.location);

      await updatePartnerApi(partnerFormData, currentUser._id);

      dispatch(partnersSlice.actions.getPartnerUpdate());
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
      throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
    }
  };
}

export function resetPassword(data) {
  return async () => {
    try {
      await axios({
        method: 'post',
        url: `/lms_user/api/direct_password_reset`,
        params: {
          new_password: data?.newPassword,
          confirm_password: data?.confirmPassword,
          user_type: data?.userType,
          reseller_id: data?.resellerId,
          user_id: data?.userId,
        },
      });
    } catch (error) {
      throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
    }
  };
}

export function manageAccount(data) {
  return async () => {
    try {
      const response = await axios({
        method: 'get',
        url: `/auth/get-access-token`,
        params: {
          user: data?.userId,
          partner: data?.partnerId,
        },
      });
      const { accessToken } = response.data;

      const res = await axios({
        method: 'get',
        url: `/auth/session-from-token`,
        params: {
          token: accessToken,
          partner: data?.partnerId,
        },
      });
      const newPartnerData = res.data;

      return newPartnerData;
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}
// export function manageAccount(data) {
//   return async () => {
//     try {
//       const response = await axios({
//         method: 'post',
//         url: `/lms_user/api/direct_manage_account`,
//         params: {
//           user_type: 'reseller',
//           reseller_id: data?.resellerId,
//           user_id: data?.userId,
//         },
//       });
//       const { access_token, refresh_token } = response.data;
//       return { access_token, refresh_token };
//     } catch (error) {
//       throw new Error(Object.keys(error.errors).map((err) => error.errors[err]));
//     }
//   };
// }

// UPDATE PARTNER PROFILE DETAILS
export function getPartnersProfileDetails() {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      const currentPartner = JSON.parse(localStorage.getItem('currentPartner'));
      dispatch(partnersSlice.actions.updatepartnerProfileDetailsSuccess(currentPartner));
    } catch (error) {
      dispatch(partnersSlice.actions.hasError(error));
    }
  };
}

export function updatepartnerProfileDetails(data, _id) {
  return async (dispatch) => {
    dispatch(partnersSlice.actions.startLoading());
    try {
      // creating partner
      const partnerFormData = new FormData();
      partnerFormData.append('name', data.name);
      partnerFormData.append('email', data.email);
      partnerFormData.append('city', data.location);
      partnerFormData.append('phone', data.phoneNumber);

      const part = await updatePartnerApi(partnerFormData, _id);
      localStorage.removeItem('currentPartner');
      localStorage.setItem('currentPartner', JSON.stringify(part));
      dispatch(partnersSlice.actions.updatepartnerProfileDetailsSuccess(part));
    } catch (error) {
      throw new Error(error?.message);
    }
  };
}
